import React, { FC, useRef, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useDispatch } from 'react-redux';
import { BasketPaymentDetailsProps } from '@components/v2/BasketPaymentDetails';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import {
    BUSINESS_NAME_REGEX,
    EMAIL_REGEX,
    NUMBER_FIELD_REGEX,
    PHONE_MAX_LENGTH,
    PHONE_REGEX,
} from '@utils/checkoutInputsValidation';
import LegalTermsAndConditionsConsent from '@components/LegalTermsAndConditionsConsent';
import Checkbox from '@components/Checkbox';
import Button from '@components/Button';
import { LEGAL_CONSENTS_VALUES } from '../../../constants/enums';
import DealService from '../../../services/deal/deal.service';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';
import { getSessionIdCookie } from '@utils/Session.utils';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import routes from '../../../constants/routes';
import { useRouter } from 'next/router';
import { theme } from '../../../styles/theme';
import Spinner from '@components/Spinner';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { ModalVersion } from '../../../constants/main';

const BasketUserDetailsTemplate: FC<BasketPaymentDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();

    const dispatch = useDispatch();

    const router = useRouter();

    const { isReinsuranceInfoEnabled } = useFeatureSwitch();

    const { currentDeal } = useCarDetailsDuck();

    const [isOpen, setIsOpen] = useState(true);

    const emailInputRef = useRef<HTMLInputElement>();
    const firstNameInputRef = useRef<HTMLInputElement>();
    const lastNameInputRef = useRef<HTMLInputElement>();
    const businessNameInputRef = useRef<HTMLInputElement>();
    const phoneInputRef = useRef<HTMLInputElement>();

    const [isLoading, setIsLoading] = useState(false);
    const [hasAllInputsFilled, setHasAllInputsFilled] = useState(false);
    const [invalidEmailError, setInvalidEmailError] = useState(false);
    const [emailRequiredError, setEmailRequiredError] = useState(false);
    const [firstNameRequiredError, setFirstNameRequiredError] = useState(false);
    const [lastNameRequiredError, setLastNameRequiredError] = useState(false);
    const [businessNameError, setBusinessNameError] = useState(false);
    const [phoneRequiredError, setPhoneRequiredError] = useState(false);
    const [invalidPhoneErrorMessageKey, setInvalidPhoneErrorMessageKey] = useState(null);

    const [showLegalRequiredErrors, setShowLegalRequiredErrors] = useState(false);
    const [selectedLegalValues, setSelectedLegalValues] = useState([]);
    const [hasConfirmedAgreement, setHasConfirmedAgreement] = useState(null);

    const inputFilledValidation = () => {
        if (phoneInputRef?.current?.value && emailInputRef?.current?.value && businessNameInputRef?.current?.value && firstNameInputRef?.current?.value && lastNameInputRef?.current?.value) {
            setHasAllInputsFilled(true);
        }
    }

    const phoneValidation = () => {
        setInvalidPhoneErrorMessageKey(null);
        setPhoneRequiredError(false);
        const phoneNumber = phoneInputRef?.current?.value;

        if (phoneNumber === '') {
            setPhoneRequiredError(true);
            return true;
        }

        if (!NUMBER_FIELD_REGEX.test(phoneNumber)) {
            setInvalidPhoneErrorMessageKey('checkout.error.numbersOnly');
            return true;
        }

        if (phoneNumber?.length !== PHONE_MAX_LENGTH) {
            setInvalidPhoneErrorMessageKey('checkout.error.phonenumber.length');
            return true;
        }

        if (!PHONE_REGEX.test(phoneNumber)) {
            setInvalidPhoneErrorMessageKey('checkout.myDetails.input.phone.error.invalid');
            return true;
        }

        inputFilledValidation();
        return false;
    };

    const emailValidation = () => {
        const email = emailInputRef?.current?.value;

        setEmailRequiredError(false);
        setInvalidEmailError(false);

        if (!EMAIL_REGEX.test(email)) {
            return setInvalidEmailError(true);
        }

        inputFilledValidation();
    };

    const businessNameValidation = () => {
        setBusinessNameError(false);

        if (!BUSINESS_NAME_REGEX.test(businessNameInputRef?.current?.value) || !businessNameInputRef?.current?.value) {
            setBusinessNameError(true);
            return true;
        }
        inputFilledValidation();
        return false;
    };

    const inputValidation = (inputRef: any = null, setInputError: any = null) => {
        if (inputRef && setInputError && inputRef?.current?.value === '') {
            return setInputError(true);
        }

        inputFilledValidation();
        return setInputError(false);
    };

    const confirm = async () => {
        inputValidation(firstNameInputRef, setFirstNameRequiredError);
        inputValidation(lastNameInputRef, setLastNameRequiredError);
        businessNameValidation();
        emailValidation();
        phoneValidation();

        setShowLegalRequiredErrors(false);
        if (
            selectedLegalValues.filter(
                (value) =>
                    !value.includes(LEGAL_CONSENTS_VALUES.AGREE) && !value.includes(LEGAL_CONSENTS_VALUES.NOT_AGREE)
            ).length > 0
        ) {
            setShowLegalRequiredErrors(true);
            return;
        }

        try {
            setIsLoading(true);

            const { data } = await DealService.finishLeasysJourney(getSessionIdCookie(), currentDeal?.token, {
                customer: {
                    email: emailInputRef.current.value,
                    phoneNumber: phoneInputRef.current.value,
                    firstName: firstNameInputRef.current.value,
                    lastName: lastNameInputRef.current.value,
                    privacyConsentStayInTouch: selectedLegalValues[0].split(':')[1] === LEGAL_CONSENTS_VALUES.AGREE,
                    privacyConsentGetBetterDeals: selectedLegalValues[1].split(':')[1] === LEGAL_CONSENTS_VALUES.AGREE,
                    privacyConsentJoinOurPartners: selectedLegalValues[2].split(':')[1] === LEGAL_CONSENTS_VALUES.AGREE,
                    businessName: businessNameInputRef.current.value,
                },
            });

            return router.push({
                pathname: `${routes.CHECKOUT_CONFIRMATION}`,
                query: {
                    dealId: currentDeal?.token,
                    isGuest: 1,
                },
            });
        } catch {
            setIsLoading(false);
            dispatch(
                UIDuck.openModal({
                    modalType: UIDuck.MODAL_TYPES.MISSING_PARTS,
                    data: {
                        title: t('modal.defaultError.title'),
                        message: t('modal.defaultError.description'),
                        buttonText: t('modal.defaultError.button'),
                    },
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );
        }
    };

    return (
        <div id="user" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.userdetails.title').toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="description">
                    <span>{t('basket.userDetails.description')}</span>
                </div>
                <div className="form">
                    <div className="input-section">
                        <span className="input-section-title">{t('checkout.myDetails.input.firstName.title')}*</span>
                        <input
                            ref={firstNameInputRef}
                            onKeyUp={() => inputValidation(firstNameInputRef, setFirstNameRequiredError)}
                            className={`input-section-input ${firstNameRequiredError ? 'error' : ''}`}
                            data-testid="checkout-user-firstName"
                            placeholder={`${t('checkout.myDetails.input.firstName.placeholder')}*`}
                        />
                        {firstNameRequiredError && (
                            <span className="input-section-error">
                                {t('checkout.myDetails.input.firstName.error.required')}
                            </span>
                        )}
                    </div>
                    <div className="input-section">
                        <span className="input-section-title">{t('checkout.myDetails.input.lastName.title')}*</span>
                        <input
                            ref={lastNameInputRef}
                            onKeyUp={() => inputValidation(lastNameInputRef, setLastNameRequiredError)}
                            data-testid="checkout-user-lastName"
                            className={`input-section-input ${lastNameRequiredError ? 'error' : ''}`}
                            placeholder={`${t('checkout.myDetails.input.lastName.placeholder')}*`}
                        />
                        {lastNameRequiredError && (
                            <span className="input-section-error">
                                {t('checkout.myDetails.input.lastName.error.required')}
                            </span>
                        )}
                    </div>
                    <div className="input-section">
                        <span className="input-section-title">{t('checkout.myDetails.input.businessName.title')}*</span>
                        <input
                            ref={businessNameInputRef}
                            onKeyUp={() => businessNameValidation()}
                            className={`input-section-input ${businessNameError ? 'error' : ''}`}
                            placeholder={`${t('checkout.myDetails.input.businessName.placeholder')}*`}
                        />
                        {businessNameError && (
                            <span className="input-section-error" data-testid="companyNameError">
                                {t('checkout.myDetails.input.businessName.error')}
                            </span>
                        )}
                    </div>
                    <div className="row-section">
                        <div className="input-section">
                            <span className="input-section-title">{t('checkout.myDetails.input.email.title')}*</span>
                            <input
                                ref={emailInputRef}
                                onKeyUp={emailValidation}
                                data-testid="checkout-user-email"
                                className={`input-section-input ${
                                    emailRequiredError || invalidEmailError ? 'error' : ''
                                }`}
                                placeholder={`${t('checkout.myDetails.input.email.placeholder')}*`}
                            />
                            {invalidEmailError && (
                                <span className="input-section-error">
                                    {t('checkout.myDetails.input.email.error.invalid')}
                                </span>
                            )}
                            {emailRequiredError && (
                                <span className="input-section-error">
                                    {t('checkout.myDetails.input.email.error.required')}
                                </span>
                            )}
                        </div>
                        <div className="input-section">
                            <span className="input-section-title">{t('checkout.myDetails.input.phone.title')}*</span>
                            <input
                                ref={phoneInputRef}
                                onKeyUp={phoneValidation}
                                className={`input-section-input ${phoneRequiredError ? 'error' : ''}`}
                                data-testid="checkout-user-phoneNumber"
                                placeholder={`${t('checkout.myDetails.input.phone.placeholder')}*`}
                            />
                            {invalidPhoneErrorMessageKey && (
                                <span className="input-section-error">{t(invalidPhoneErrorMessageKey)}</span>
                            )}
                            {phoneRequiredError && (
                                <span className="input-section-error">
                                    {t('checkout.myDetails.input.phone.error.invalid')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="required-section">
                        <p>*{t('checkout.myDetails.required.label')}</p>
                    </div>
                </div>
                <LegalTermsAndConditionsConsent
                    showRequiredErrors={showLegalRequiredErrors}
                    setSelectedLegalValues={setSelectedLegalValues}
                    withoutDescription
                />
                <Checkbox
                    name="letter"
                    checked={hasConfirmedAgreement}
                    onChange={() => setHasConfirmedAgreement(!hasConfirmedAgreement)}
                >
                    {t('basket.userDetails.agreement')}
                </Checkbox>
                <Button
                    primary
                    centerMode
                    marginTop={20}
                    onClick={confirm}
                    disabled={
                        !hasAllInputsFilled ||
                        !hasConfirmedAgreement ||
                        invalidPhoneErrorMessageKey ||
                        phoneRequiredError ||
                        firstNameRequiredError ||
                        lastNameRequiredError ||
                        businessNameError ||
                        emailRequiredError ||
                        invalidEmailError ||
                        !firstNameInputRef?.current?.value ||
                        !lastNameInputRef?.current?.value ||
                        !businessNameInputRef?.current?.value ||
                        !emailInputRef?.current?.value ||
                        !phoneInputRef?.current?.value
                    }
                >
                    {isLoading ? (
                        <Spinner size={15} border={3} color={theme.colors.white} />
                    ) : (
                        t('basket.userDetails.confirm')
                    )}
                </Button>
                {isReinsuranceInfoEnabled && (
                    <button className="reinsurance">{t('continue.cta.reinsurance.text')}</button>
                )}
            </Collapsible>
        </div>
    );
};

export default BasketUserDetailsTemplate;
